import React, {useState, useEffect, useMemo} from 'react';
import {
  Box,
  Text,
  Image,
  Link,
  BoxProps,
  Flex,
  useTheme,
  AspectRatio,
  HStack,
  VStack,
  Button,
} from '@chakra-ui/react';
import {MapContainer, TileLayer, Marker, Popup, useMap} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import mapStyles, {leafletPopupStyles} from './styles';
import {
  createLocationMarkerIcon,
  createUserLocationIcon,
  createParkingMarkerIcon,
  createFarmersMarketCenterIcon,
} from './markers';
import {getDistanceString} from '../../utils/distance';
import {useNavigate} from 'react-router-dom';
import {TextG12, TextG10, TextG16} from '../typography';
import useGetUserLocation from '../../hooks/useUserLocation';
import {FullscreenControl} from './fullScreenControl';
import getVenueDistanceText from '../../utils/venueDistance';
import useOfferModal from '../../hooks/useOfferModal';
import {getDirectionsLink} from '../../utils/address';
import {BlueButton} from '../button';

// Define the interface for location objects
export interface Location {
  id?: string | number;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  actionLink?: string;
  featured?: boolean;
}

export interface MapViewProps extends BoxProps {
  articles: any[];
  userLocation?: {latitude: number; longitude: number};
  height?: string | number | object; // Allow responsive height object
  initialZoom?: number;
  fitBounds?: boolean;
  isMobile?: boolean; // Mobile optimization flag
  center?: {latitude: number; longitude: number}; // Optional specific map center
}

// Fix the default icon issue in Leaflet
const fixLeafletIcon = () => {
  // Fix the default icon paths that get broken in production builds
  // This is a known issue with Leaflet + Webpack
  // @ts-ignore - This is a workaround for a known Leaflet issue
  delete L.Icon.Default.prototype._getIconUrl;

  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  });
};

// Component to handle map bounds and centering
const MapBoundsSetter = ({
  locations,
  userLocation,
  initialZoom = 13,
  fitBounds = false,
  center, // Add center parameter
}: {
  locations: Location[];
  userLocation?: {latitude: number; longitude: number};
  initialZoom?: number;
  fitBounds?: boolean;
  center?: {latitude: number; longitude: number}; // Add center type
}) => {
  const map = useMap();

  // Use a ref to track if we've already set the initial view
  const initialViewSetRef = React.useRef(false);

  useEffect(() => {
    // Reset the ref when center changes to ensure we respond to new center values
    // if (center) {
    //   initialViewSetRef.current = false;
    // }

    // Only set the view once for a given center value
    if (initialViewSetRef.current) {
      return; // Skip if we've already set the initial view for this center
    }

    // Mark that we've set the initial view
    initialViewSetRef.current = true;

    // If a specific center is provided, use it with priority - ALWAYS respect this first
    // if (center) {
    //   console.log('Setting map center to explicit coordinates:', center);
    //   map.setView([center.latitude, center.longitude], initialZoom);
    //   return;
    // }

    // If fitBounds is true and we have locations, calculate bounds based ONLY on locations (not user location)
    if (fitBounds && locations.length > 0) {
      const bounds: L.LatLngBoundsExpression = [];

      // Add all location points to bounds (exclude user location)
      locations.forEach((location) => {
        bounds.push([location.latitude, location.longitude]);
      });

      // Only fit bounds if we have locations
      if (bounds.length > 0) {
        if (bounds.length === 1) {
          // If only one point, center on it with the initial zoom
          console.log('Setting map center to single location:', bounds[0]);
          map.setView(bounds[0] as L.LatLngExpression, initialZoom);
        } else {
          // Fit map to include all location points with some padding
          console.log('Fitting map bounds to all locations');
          map.fitBounds(bounds, {padding: [50, 50]});
        }
      }
    } else if (locations.length > 0) {
      // If not fitting bounds but we have locations, center on the average position
      if (locations.length === 1) {
        // If only one location, center on it
        //console.log('Setting map center to only location:', [locations[0].latitude, locations[0].longitude]);
        map.setView([locations[0].latitude, locations[0].longitude], initialZoom);
      } else {
        // Calculate the center of all location points (NOT including user location)
        let sumLat = 0;
        let sumLng = 0;

        locations.forEach((location) => {
          sumLat += location.latitude;
          sumLng += location.longitude;
        });

        const centerLat = sumLat / locations.length;
        const centerLng = sumLng / locations.length;

        console.log('Setting map center to calculated center of all locations:', [
          centerLat,
          centerLng,
        ]);
        map.setView([centerLat, centerLng], initialZoom);
      }
    } else if (userLocation) {
      // ONLY use user location as a last resort if no locations are provided
      console.log('No locations available, falling back to user location for map center');
      map.setView([userLocation.latitude, userLocation.longitude], initialZoom);
    }
    // Add center to the dependency array to respond to center changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, locations]); // Respond when center or locations change

  return null;
};

const MapView: React.FC<MapViewProps> = ({
  articles,
  userLocation: userLocationProp,
  height = '400px',
  initialZoom = 18,
  fitBounds = true,
  isMobile = false,
  center,
  ...restProps
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const hookUserLocation = useGetUserLocation();
  const {showOfferModal} = useOfferModal();

  // Determine the actual user location to use
  const userLocation =
    userLocationProp ||
    (hookUserLocation?.data
      ? {
          latitude: hookUserLocation.data.latitude,
          longitude: hookUserLocation.data.longitude,
        }
      : undefined);
  // Filter out custom props from rest props to prevent DOM warnings
  const {
    userLocation: _,
    isMobile: __,
    highlightNearby: ___,
    center: ____,
    touchAction: _____,
    ...boxProps
  } = restProps as any; // Calculate venue distances if user location is available
  const venuesWithDistances = useMemo(() => {
    if (userLocation && articles) {
      return articles.map((article) => {
        if (article && article.latitude && article.longitude) {
          const distanceText = getVenueDistanceText(userLocation, {
            latitude: article.latitude,
            longitude: article.longitude,
          });
          return {
            ...article,
            distanceText,
          };
        }
        return article;
      });
    }
    return articles;
  }, [articles, userLocation]);

  // Use our custom marker icons instead of the basic icon
  // This has been replaced with the createLocationMarkerIcon function
  const markers = useMemo(() => {
    return (
      venuesWithDistances &&
      venuesWithDistances.map((article, index) => {
        if (article && article.latitude && article.longitude) {
          const distance = article.distance ? getDistanceString(article.distance) : null;
          const type = typeof article.type === 'string' ? article.type.toLowerCase() : 'place';

          let markerIcon;

          if (article.customIcon === 'P') {
            markerIcon = createParkingMarkerIcon();
          } else if (article.customIcon === 'FARMERS_MARKET_CENTER') {
            markerIcon = createFarmersMarketCenterIcon();
          } else {
            markerIcon = createLocationMarkerIcon(
              article.number ? article.number - 1 : index,
              article.pinColor || '#276ACD' // Use custom color or default to brand blue
            );
          }

          return (
            <Marker
              icon={markerIcon}
              key={
                article.id
                  ? article.id
                  : `marker-${index}-${article.name.toLowerCase().replace(/\s+/g, '-')}`
              }
              position={[article.latitude, article.longitude]}>
              <Popup
                minWidth={isMobile ? 80 : 100}
                maxWidth={isMobile ? 160 : 200}
                closeButton={true}
                autoPan={true}
                keepInView={true}>
                <Box sx={mapStyles.popupContainer}>
                  <HStack
                    onClick={() => {
                      if (article.type === 'offer') {
                        showOfferModal({offer: article as any});
                      } else {
                        navigate(
                          article.actionLink ? article.actionLink : `/${type}/${article.id}`
                        );
                      }
                    }}
                    _hover={{}}
                    _active={{}}
                    minH={isMobile ? '44px' : 'auto'} // Touch-friendly minimum height
                    p={isMobile ? 1 : 0}>
                    {article.image_url && (
                      <Image
                        objectFit="cover"
                        borderRadius="full"
                        w={isMobile ? '50px' : '60px'}
                        h={isMobile ? '50px' : '60px'}
                        minH={isMobile ? '50px' : '60px'}
                        minW={isMobile ? '50px' : '60px'}
                        src={article.image_url}
                      />
                    )}
                    <VStack alignItems="flex-start" spacing={1}>
                      <TextG12
                        noOfLines={2}
                        isTruncated="&hellip;"
                        margin="0 !important"
                        fontSize={isMobile ? 'sm' : 'md'}
                        lineHeight="short"
                        fontWeight="bold"
                        dangerouslySetInnerHTML={{__html: article.name || article.title}}>
                        {}
                      </TextG12>
                      <TextG10
                        marginTop="0 !important"
                        isTruncated="&hellip;"
                        noOfLines={4}
                        fontSize={isMobile ? 'xs' : 'sm'}
                        dangerouslySetInnerHTML={{
                          __html: article?.description || article?.summary,
                        }}>
                        {}
                      </TextG10>
                      {(article.distanceText || distance) && (
                        <TextG10
                          marginTop="0.25rem !important"
                          color="grey"
                          fontSize={isMobile ? 'xs' : 'sm'}>
                          {article.distanceText || `${distance} away`}
                        </TextG10>
                      )}
                    </VStack>
                  </HStack>

                  {/* Get Directions Button */}
                  {article.latitude && article.longitude && (
                    <Box mt={2} pt={2} borderTop="1px solid #e2e8f0">
                      <BlueButton
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the main click handler
                          window.open(
                            getDirectionsLink({
                              latitude: article.latitude,
                              longitude: article.longitude,
                            }),
                            '_blank',
                            'noopener,noreferrer'
                          );
                        }}
                        width="100%">
                        <TextG16 color="white !important">Get Directions</TextG16>
                      </BlueButton>
                    </Box>
                  )}
                </Box>
              </Popup>
            </Marker>
          );
        }
      })
    );
  }, [articles, navigate, isMobile]);

  // Fix the Leaflet icon issue
  useEffect(() => {
    fixLeafletIcon();
  }, []);
  // Create location markers icons
  const userLocationIcon = createUserLocationIcon();

  // Apply custom CSS for leaflet popups
  useEffect(() => {
    // Create a style element for leaflet custom styles
    const styleElement = document.createElement('style');
    styleElement.textContent = leafletPopupStyles;
    document.head.appendChild(styleElement);

    // Cleanup on unmount
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  return (
    <Box
      height={height}
      width="100%"
      borderRadius={{base: 'md', md: 'lg'}}
      overflow="hidden"
      position="relative"
      sx={{
        ...mapStyles.mapContainer,
        touchAction: isMobile ? 'pan-x pan-y' : 'auto', // Enable touch scrolling on mobile
      }}
      data-map-venues={JSON.stringify(articles)} // Add venue data as data attribute
      {...boxProps}>
      {/* We need this container to have a specific height */}{' '}
      <MapContainer
        style={{height: '100%', width: '100%'}}
        // Use center prop if available, otherwise use a default center that will be overridden
        center={center ? [center.latitude, center.longitude] : [32.7492, -117.12985]}
        zoom={initialZoom}
        maxZoom={18}
        minZoom={13}
        attributionControl={false}
        zoomControl={!isMobile} // Hide zoom controls on mobile to save space
        scrollWheelZoom={!isMobile} // Disable scroll wheel zoom on mobile
        doubleClickZoom={true}
        touchZoom={isMobile}
        dragging={true}>
        {' '}
        {/* Map bounds handler */}
        {/* <MapBoundsSetter
          locations={articles.filter(article => article && article.latitude && article.longitude)}
          userLocation={userLocation}
          initialZoom={initialZoom}
          //fitBounds={fitBounds && !center} // Only fit bounds if no explicit center is provided
          //center={center}
        /> */}
        {/* OpenStreetMap tile layer */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/* Render location markers */}
        {markers}
        {/* Render user's location if available */}
        {userLocation && (
          <Marker
            position={[userLocation.latitude, userLocation.longitude]}
            icon={userLocationIcon}>
            <Popup minWidth={150} maxWidth={250} closeButton={true} autoPan={true}>
              <Box sx={mapStyles.popupContainer} p={isMobile ? 2 : 3}>
                <Text sx={mapStyles.popupTitle} fontSize={isMobile ? 'sm' : 'md'} fontWeight="bold">
                  You're here!
                </Text>
                <Text
                  sx={mapStyles.popupDescription}
                  fontSize={isMobile ? 'xs' : 'sm'}
                  mt={1}
                  lineHeight="short">
                  This is your current location. Explore the nearby spots where neighbors gather!
                </Text>
              </Box>
            </Popup>
          </Marker>
        )}
        <FullscreenControl />
      </MapContainer>
      {/* Attribution displayed outside to maintain design consistency */}
      <Box
        zIndex="999"
        sx={mapStyles.attribution}
        fontSize={isMobile ? 'xs' : 'sm'}
        p={isMobile ? 1 : 2}>
        &copy; OpenStreetMap contributors
      </Box>
    </Box>
  );
};

export default MapView;
